import { getGeminiKey } from './appConfig';
import {
  buildLengthDirective,
  extractExactDataBlock,
  sanitizeOutputLinks,
} from './aiResponseHelpers';

// Regla crítica compartida: la IA NUNCA debe inventar enlaces, códigos ni fechas.
const INTEGRITY_RULE = `REGLA CRÍTICA (PRIORIDAD MÁXIMA):
- NUNCA inventes, modifiques ni "recrees" enlaces (URLs), enlaces de grupo de WhatsApp, enlaces de pago, números de cuenta/operación ni fechas.
- Cópialos EXACTAMENTE, carácter por carácter, desde la BASE DE CONOCIMIENTO / DATOS EXACTOS.
- Cuando un cliente pregunta por un curso, usa el enlace del snippet cuyo nombre y fecha coincidan con ese curso.
- Si no encuentras el enlace o dato exacto en la base, NO lo inventes: dilo y pide confirmar con un asesor.`;

const GEMINI_MODEL = 'gemini-2.5-flash';
const GEMINI_URL = `https://generativelanguage.googleapis.com/v1beta/models/${GEMINI_MODEL}:generateContent`;
const SERPER_API_KEY = process.env.REACT_APP_SERPER_API_KEY || '';

// ============================================
// CONOCIMIENTO WORDPRESS / WOOCOMMERCE / TUTOR LMS
// ============================================
const WP_WOOCOMMERCE_TUTOR_KNOWLEDGE = `
CONOCIMIENTO TÉCNICO — WORDPRESS / WOOCOMMERCE / TUTOR LMS:

=== WORDPRESS ===
- Panel de administración: wp-admin (Ajustes, Plugins, Usuarios, Apariencia, Herramientas)
- Gestión de usuarios: wp-admin/users.php
  * Roles: Administrator, Editor, Author, Subscriber, Customer, Instructor, Student
  * Agregar usuario: Usuarios > Añadir nuevo > asignar rol
  * Verificar matrícula: buscar email en Usuarios, revisar rol y cursos asignados
- Plugins esenciales: WooCommerce, Tutor LMS, Elementor, Contact Form 7, WP Mail SMTP
- Problemas comunes y soluciones:
  * "No puedo acceder al sitio" → Verificar credenciales en wp-login.php, limpiar caché del navegador, verificar si la cuenta existe
  * "Pantalla en blanco (White Screen)" → Desactivar plugins vía FTP (/wp-content/plugins/), aumentar PHP memory_limit en wp-config.php
  * "El sitio va lento" → Optimizar imágenes (Smush/ShortPixel), configurar caché (LiteSpeed/WP Super Cache), usar CDN (Cloudflare)
  * "Error 500 Internal Server" → Revisar .htaccess (renombrar y regenerar), revisar PHP error_log, verificar límites del servidor
  * "Error de conexión a la base de datos" → Verificar credenciales en wp-config.php (DB_NAME, DB_USER, DB_PASSWORD, DB_HOST)
  * "No llegan emails" → Instalar WP Mail SMTP, configurar SMTP con Gmail/SendGrid/Mailgun
  * "Actualizaciones rotas" → Verificar permisos de archivos (755 carpetas, 644 archivos), desactivar plugins, actualizar manualmente vía FTP

=== WOOCOMMERCE ===
- Gestión de pedidos: wp-admin/edit.php?post_type=shop_order
- Estados: Pendiente de pago → Procesando → Completado | Cancelado | Reembolsado | Fallido
- Cupones: wp-admin/edit.php?post_type=shop_coupon (% descuento, monto fijo, envío gratis)
- Productos: Simples, Variables (tallas/colores), Virtuales, Descargables
- Pasarelas de pago: PayPal, Stripe, MercadoPago, Culqi, Niubiz, transferencia bancaria
- Problemas comunes:
  * "No llegan emails de pedido" → WooCommerce > Ajustes > Emails > verificar destinatario y plantilla
  * "Error en el checkout" → Revisar pasarela de pago, verificar SSL activo, desactivar plugins conflictivos
  * "Producto no visible" → Verificar estado (publicado/borrador), stock disponible, visibilidad del catálogo
  * "Precio incorrecto" → Verificar precio regular vs precio rebajado, cupones activos, impuestos configurados
  * "No puedo descargar mi compra" → Verificar estado del pedido (debe ser "Completado"), permisos de descarga en el producto

=== TUTOR LMS ===
- Panel del instructor: /dashboard/ (gestión completa de cursos)
- Crear cursos: Dashboard > Mis Cursos > Añadir Nuevo
- Estructura de curso: Curso > Temas (módulos) > Lecciones + Quizzes
  * Tipos de lección: Video (YouTube, Vimeo, self-hosted), Texto/HTML, Zoom Meeting, Google Meet, Assignment
  * Quizzes: Opción múltiple, verdadero/falso, respuesta corta, ordenar, completar, ensayo
- Matrícula de estudiantes:
  * Manual: Tutor LMS > Estudiantes > Matricular manualmente por email
  * Automática: Compra vía WooCommerce (producto vinculado al curso)
  * Verificar matrícula: Dashboard > Mis Cursos > ver estudiantes inscritos, o buscar usuario en wp-admin
- Problemas comunes:
  * "No veo mis cursos" → Verificar matrícula activa, estado del curso (publicado), rol del usuario (Student)
  * "No puedo ver las lecciones" → Verificar si curso es gratuito o de pago, matrícula activa, prerequisitos cumplidos
  * "Error al subir video" → Verificar límites PHP: upload_max_filesize (128M), post_max_size (128M), max_execution_time (300)
  * "No puedo rendir el examen" → Verificar prerequisitos, intentos disponibles, fecha límite del quiz
  * "Mi progreso no se guarda" → Limpiar caché del navegador, verificar que las lecciones se marquen como completadas
  * "No recibí certificado" → Configurar en Tutor LMS > Settings > Certificate, verificar que completó 100% del curso
  * "Zoom no funciona" → Verificar API Key y Secret de Zoom en Tutor LMS > Settings > Zoom, reunión creada correctamente
- Configuración clave:
  * Monetización: Tutor LMS > Settings > Monetization > WooCommerce
  * Certificados: Tutor LMS > Settings > Certificate (activar y personalizar)
  * Email: Tutor LMS > Settings > Email Notification (matrícula, completado, quiz)
  * Acceso: Content Drip (liberar contenido gradualmente)

=== FLUJO DE SOPORTE TÉCNICO ===
1. IDENTIFICAR: ¿Problema de acceso, contenido, pago, o técnico?
2. RECOPILAR: Email del usuario, URL del sitio, capturas de pantalla, pasos para reproducir
3. DIAGNOSTICAR: Verificar en wp-admin (usuario, pedido, curso, matrícula)
4. RESOLVER: Aplicar solución directa o escalar al equipo técnico
5. CONFIRMAR: Verificar con el usuario que el problema se resolvió
6. DOCUMENTAR: Guardar la solución en el Gestor de Respuestas para futuras consultas
`;

/**
 * Llamar a la API de Gemini con reintentos y backoff exponencial
 * Soporta texto e imágenes en base64
 */
export const callGemini = async (prompt, systemInstruction, imageData = null, retries = 3, genConfig = {}) => {
  for (let i = 0; i < retries; i++) {
    try {
      // Construir las partes del contenido (texto + imagen opcional)
      const parts = [{ text: prompt }];

      if (imageData) {
        // imageData debe ser en formato data:image/...;base64,...
        // Extraer el tipo de imagen y datos base64
        const [header, data] = imageData.split(',');
        const mimeType = header.match(/:(.*?);/)[1]; // Extraer "image/jpeg" etc

        parts.push({
          inlineData: {
            mimeType: mimeType,
            data: data
          }
        });
      }

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 45000); // 45s timeout

      let response;
      try {
        response = await fetch(`${GEMINI_URL}?key=${getGeminiKey()}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          signal: controller.signal,
          body: JSON.stringify({
            contents: [
              {
                parts: parts
              }
            ],
            systemInstruction: {
              parts: [{ text: systemInstruction }]
            },
            generationConfig: {
              temperature: 0.7,
              maxOutputTokens: 8192,
              ...genConfig
            }
          })
        });
      } finally {
        clearTimeout(timeoutId);
      }

      // Manejo de errores con reintentos
      if (response.status === 429 || response.status === 500) {
        const delay = Math.pow(2, i) * 1000; // Backoff exponencial
        await new Promise(resolve => setTimeout(resolve, delay));
        continue;
      }

      if (!response.ok) {
        throw new Error(`Error de API: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();

      if (data.candidates && data.candidates.length > 0) {
        return data.candidates[0].content.parts[0].text;
      } else {
        // Manejar respuesta sin candidato (ej. filtro de seguridad)
        if (data.promptFeedback && data.promptFeedback.blockReason) {
          console.error('Respuesta bloqueada por Gemini:', data.promptFeedback.blockReason);
          throw new Error(`Respuesta bloqueada: ${data.promptFeedback.blockReason}`);
        }
        throw new Error('No se recibió respuesta de Gemini');
      }
    } catch (error) {
      // Timeout o abort: no reintentar
      if (error.name === 'AbortError') {
        throw new Error('La solicitud a Gemini tardó demasiado (>45s). Intenta con menos eventos.');
      }
      console.error(`Intento ${i + 1} fallido:`, error);
      if (i === retries - 1) {
        throw error;
      }
    }
  }

  throw new Error('Se agotaron los reintentos');
};

// ============================================
// BÚSQUEDA WEB (Serper API)
// ============================================

/**
 * Buscar en internet usando Serper API
 * @returns {string|null} Resultados formateados o null
 */
export const searchWeb = async (searchQuery) => {
  if (!SERPER_API_KEY) return null;
  try {
    const response = await fetch('https://google.serper.dev/search', {
      method: 'POST',
      headers: { 'X-API-KEY': SERPER_API_KEY, 'Content-Type': 'application/json' },
      body: JSON.stringify({ q: searchQuery, gl: 'pe', hl: 'es', num: 5 })
    });
    if (!response.ok) return null;
    const data = await response.json();
    const results = (data.organic || []).slice(0, 5).map((r, i) =>
      `${i + 1}. ${r.title}\n   ${r.snippet}\n   Fuente: ${r.link}`
    ).join('\n\n');
    return results || null;
  } catch (error) {
    console.error('Web search error:', error);
    return null;
  }
};

/**
 * Determinar si una pregunta requiere búsqueda web
 */
export const shouldSearchWeb = async (userMessage) => {
  if (!SERPER_API_KEY) return { needsSearch: false, searchQuery: null };
  const systemInstruction = `Determina si esta pregunta requiere información actualizada de internet. Responde SOLO con JSON sin markdown:
{"needsSearch": true/false, "searchQuery": "query para Google" o null}

NECESITA búsqueda web cuando:
- Pregunta por precios actuales, tarifas o costos de servicios externos
- Pregunta por documentación técnica específica y actualizada
- Pregunta por regulaciones, leyes o normativas vigentes
- Pregunta por comparativas de herramientas/servicios actuales
- Pregunta sobre actualizaciones recientes de WordPress/plugins

NO necesita búsqueda web cuando:
- Es pregunta sobre el negocio/empresa (la base de conocimiento tiene esa info)
- Es soporte técnico genérico de WordPress/WooCommerce/Tutor LMS
- Pide redactar o generar contenido
- Es conversación casual, saludo o pregunta personal
- Es pregunta que se puede responder con conocimiento general`;

  try {
    const result = await callGemini(userMessage, systemInstruction, null);
    const clean = result.replace(/```json/g, '').replace(/```/g, '').trim();
    return JSON.parse(clean);
  } catch {
    return { needsSearch: false, searchQuery: null };
  }
};

// ============================================
// MEMORIA PERSISTENTE - EXTRACCIÓN
// ============================================

/**
 * Extraer hechos/instrucciones/correcciones del turno de conversación
 * Se ejecuta de forma asíncrona después de cada respuesta (fire-and-forget)
 */
export const extractMemoryFromConversation = async (userMessage, aiResponse, existingMemory = []) => {
  const existingFacts = existingMemory.slice(0, 30).map(m => m.content).join('\n- ');

  const systemInstruction = `Eres un extractor de memoria. Analiza esta conversación y extrae SOLO información nueva que valga la pena recordar para futuras conversaciones.

MEMORIA EXISTENTE (NO duplicar):
${existingFacts ? '- ' + existingFacts : '(vacía)'}

REGLAS:
- Solo extrae información NUEVA y ÚTIL que no esté en la memoria existente
- Máximo 3 items por turno
- Cada item debe ser conciso (1-2 oraciones máximo)
- Categoriza: "fact" (dato), "instruction" (cómo hacer algo), "correction" (corrección del usuario), "preference" (preferencia del usuario)
- Si NO hay nada nuevo que recordar, responde: []
- Responde SOLO con JSON array, sin markdown ni explicaciones

Formato: [{"content": "texto conciso", "category": "fact|instruction|correction|preference"}]`;

  const prompt = `USUARIO: "${userMessage}"\n\nASISTENTE: "${aiResponse.substring(0, 800)}"`;

  try {
    const result = await callGemini(prompt, systemInstruction, null);
    const clean = result.replace(/```json/g, '').replace(/```/g, '').trim();
    const items = JSON.parse(clean);
    return Array.isArray(items) ? items.slice(0, 3) : [];
  } catch {
    return [];
  }
};

/**
 * Generar contenido basado en un título
 */
export const generateContent = async (title) => {
  const systemInstruction = `Eres un asistente de profesor amigable y profesional.
Genera un mensaje cordial y útil para un estudiante basado en el título proporcionado.
El mensaje debe ser:
- Claro y conciso (máximo 200 palabras)
- Motivador y educativo
- Con un tono profesional pero cercano
- Incluir 2-3 emojis apropiados
- En español`;

  return await callGemini(title, systemInstruction, null);
};

/**
 * Mejorar un texto existente
 */
export const improveContent = async (content) => {
  const systemInstruction = `Eres un editor experto. Mejora el siguiente texto:
- Corrige gramática y ortografía
- Mejora la claridad y coherencia
- Mantén un tono profesional pero amigable
- Añade emojis apropiados si no los tiene (máximo 3)
- Mantén el mensaje conciso y directo
- Conserva la intención original del mensaje
- Responde en español`;

  return await callGemini(content, systemInstruction, null);
};

/**
 * Generar respuesta para WhatsApp basada en todas las notas del usuario
 * Analiza el contexto de todas las respuestas guardadas y opcionalmente procesa una imagen
 */
export const generateWhatsAppResponse = async (userMessage, allNotes = [], aiSettings = {}, imagePreview = null) => {
  // RAG-lite: contenido COMPLETO de los snippets más relevantes (los enlaces van completos)
  const relevant = getRelevantSnippetsLocally(userMessage, allNotes, 8);
  const notesContext = relevant.length > 0
    ? `\n\nRESPUESTAS GUARDADAS MÁS RELEVANTES (contenido completo):\n${relevant.map((note, idx) =>
      `--- RESPUESTA #${idx + 1} ---\n[${note.category}] ${note.title}\n${note.content}`
    ).join('\n\n')}`
    : '';

  // Datos exactos (enlaces/fechas) — copiar literal
  const exactDataBlock = extractExactDataBlock(relevant);
  const exactDataSection = exactDataBlock
    ? `\n\nDATOS EXACTOS — COPIA ESTOS ENLACES/DATOS TAL CUAL (no los modifiques):\n${exactDataBlock}`
    : '';

  // Construir contexto de información de empresa
  const { companyName = '', companyRUC = '', companyInfo = '', customPrompt = '', responseLength = 'normal' } = aiSettings;
  const { directive: lengthDirective, maxOutputTokens } = buildLengthDirective(responseLength);
  let companyContext = '';

  if (companyName || companyRUC || companyInfo) {
    companyContext = '\n\nInformación de la empresa:';
    if (companyName) companyContext += `\n- Nombre: ${companyName}`;
    if (companyRUC) companyContext += `\n- RUC/ID: ${companyRUC}`;
    if (companyInfo) companyContext += `\n- Descripción: ${companyInfo}`;
  }

  // Construir instrucción sobre la imagen si existe
  let imageContext = '';
  if (imagePreview) {
    imageContext = '\n\n📸 NOTA IMPORTANTE: El usuario también ha adjuntado una imagen. Analiza el contenido de la imagen y considéralo en tu respuesta.';
  }

  const systemInstruction = `Eres un asistente de soporte técnico y atención al cliente especializado en educación y negocios.

${INTEGRITY_RULE}

IMPORTANTE: Analiza el CONTEXTO y la INTENCIÓN del mensaje, NO solo las palabras literales.

Tu objetivo es:
1. Analizar el mensaje del usuario${imagePreview ? ' y la imagen adjunta' : ''} para identificar:
   - ¿Es una PREGUNTA de información? (precios, horarios, etc.)
   - ¿Es un PROBLEMA técnico? (no veo cursos, error al acceder, etc.)
   - ¿Es una SOLICITUD? (quiero inscribirme, necesito ayuda, etc.)

2. PROBLEMAS TÉCNICOS COMUNES (prioridad alta):
   - "No veo cursos en mi aula virtual" → Solicitar correo electrónico para revisar acceso en WordPress
   - "No puedo acceder" → Verificar credenciales y solicitar correo
   - "Error al cargar" → Reportar al equipo técnico y solicitar detalles

3. Consultar el contexto de respuestas guardadas para:
   - Buscar soluciones específicas a problemas similares
   - Mantener coherencia con políticas de la empresa
   - Usar información de contacto correcta

4. Generar una respuesta:
   - Profesional pero empática y cercana
   - Clara y accionable (decir qué hacer a continuación)
   - Con 2-3 emojis apropiados
   - En español
   - ${lengthDirective}

${notesContext}${exactDataSection}
${companyContext}
${imageContext}

Directrices críticas:
- Si es un PROBLEMA TÉCNICO, NO des información genérica de cursos
- Sé ESPECÍFICO sobre los pasos a seguir
- Si necesitas datos del usuario (correo, etc.), PÍDELOS explícitamente
- Muestra empatía: "Entiendo tu situación", "Estoy revisando tu caso"
- Da tiempos estimados si aplica: "En las próximas 24 horas"
- Incluye contacto de soporte si es necesario${customPrompt ? `\n\nINSTRUCCIÓN PERSONALIZADA DEL USUARIO (PRIORITARIA, respétala salvo la REGLA CRÍTICA): ${customPrompt}` : ''}`;

  const prompt = `Genera una respuesta profesional para este mensaje de WhatsApp:\n\n"${userMessage}"`;

  // temperature baja para no inventar enlaces/datos; corrige enlaces contra la base
  const raw = await callGemini(prompt, systemInstruction, imagePreview, 3, { temperature: 0.25, maxOutputTokens });
  return sanitizeOutputLinks(raw, relevant).text;
};

/**
 * Generar un título automático basado en una respuesta
 * Analiza el contenido y contexto para sugerir un título adecuado
 */
export const generateTitleForResponse = async (response, userMessage = '') => {
  const systemInstruction = `Eres un experto en crear títulos concisos y descriptivos.
Tu objetivo es analizar una respuesta y generar un título breve que resuma su contenido principal.

Requisitos:
- Título muy conciso (máximo 15 palabras)
- Descriptivo y relevante al contenido
- Profesional y claro
- En español
- Sin emojis

Solo responde con el título, nada más.`;

  const prompt = `Mensaje original: "${userMessage}"\n\nRespuesta: "${response}"\n\nGenera un título corto y descriptivo para esta respuesta:`;

  return await callGemini(prompt, systemInstruction, null);
};

/**
 * Búsqueda local de snippets relevantes (sin API call extra)
 * Usa scoring por keyword overlap para encontrar los más relevantes
 */
const getRelevantSnippetsLocally = (userMessage, allNotes, maxResults = 12) => {
  if (!userMessage || allNotes.length === 0) return allNotes.slice(0, maxResults);

  const words = userMessage.toLowerCase().split(/\s+/).filter(w => w.length > 2);
  if (words.length === 0) return allNotes.slice(0, maxResults);

  const scored = allNotes.map(note => {
    const haystack = `${note.title} ${note.content} ${note.category}`.toLowerCase();
    let score = 0;
    for (const word of words) {
      if (haystack.includes(word)) score++;
    }
    return { note, score };
  });

  scored.sort((a, b) => b.score - a.score);
  return scored.slice(0, maxResults).map(s => s.note);
};

/**
 * Generar respuesta de chat multi-turno con RAG-lite
 * Función principal del Super Agente IA
 */
export const generateChatResponse = async (messages, allNotes = [], aiSettings = {}, imagePreview = null, memoryEntries = [], webSearchResults = null) => {
  const { companyName = '', companyRUC = '', companyInfo = '', customPrompt = '', responseLength = 'normal', messageCard = false } = aiSettings;
  const { directive: lengthDirective, maxOutputTokens } = buildLengthDirective(responseLength);
  // Solo el chat Gestor pide la tarjeta de mensaje (estilo ChatGPT). Notas/otros no.
  const gestorFormatDirective = messageCard
    ? `\n- FORMATO DE SALIDA: Cuando generes un mensaje/respuesta para ENVIAR a un cliente, escribe primero UNA sola línea breve de introducción (ej. "Aquí tienes la respuesta:") y luego el mensaje LISTO PARA ENVIAR delimitado EXACTAMENTE así, cada marcador en su propia línea:
<<<MENSAJE>>>
(aquí el mensaje completo tal como se enviará: saludo, contenido, enlaces EXACTOS y emojis)
<<<FIN>>>
Dentro de los marcadores NO pongas comillas, etiquetas ni explicaciones. Si la consulta es una duda interna (no un mensaje para cliente), responde normal SIN marcadores.`
    : '';

  // Obtener el último mensaje del usuario para RAG
  const lastUserMsg = [...messages].reverse().find(m => m.role === 'user');
  const lastUserText = lastUserMsg?.content || '';

  // RAG-lite: buscar snippets relevantes localmente
  const relevant = getRelevantSnippetsLocally(lastUserText, allNotes, 12);

  // Tier 1: Contenido completo de los más relevantes
  const deepContext = relevant.length > 0
    ? relevant.map((note, idx) =>
      `--- RESPUESTA #${idx + 1} ---\n[${note.category}] ${note.title}\n${note.content}`
    ).join('\n\n')
    : 'No hay respuestas guardadas aún.';

  // Tier 2: Catálogo de títulos de TODOS
  const catalog = allNotes.length > 0
    ? allNotes.map((n, i) => `${i + 1}. [${n.category}] ${n.title}`).join('\n')
    : '';

  // Datos exactos (enlaces/fechas) extraídos de los snippets relevantes — copiar literal
  const exactDataBlock = extractExactDataBlock(relevant);
  const exactDataSection = exactDataBlock
    ? `\nDATOS EXACTOS — COPIA ESTOS ENLACES/DATOS TAL CUAL (no los modifiques):\n${exactDataBlock}\n`
    : '';

  let companyContext = '';
  if (companyName || companyRUC || companyInfo) {
    companyContext = '\n\nINFORMACIÓN DE LA EMPRESA:';
    if (companyName) companyContext += `\n- Nombre: ${companyName}`;
    if (companyRUC) companyContext += `\n- RUC/ID: ${companyRUC}`;
    if (companyInfo) companyContext += `\n- Descripción: ${companyInfo}`;
  }

  // Construir bloque de memoria persistente
  const recentMemory = (memoryEntries || []).slice(0, 50);
  const memoryBlock = recentMemory.length > 0
    ? `\nMEMORIA PERSISTENTE (${recentMemory.length} recuerdos de conversaciones anteriores):
${recentMemory.map((m, i) => `${i + 1}. [${m.category || 'fact'}] ${m.content}`).join('\n')}
IMPORTANTE: Usa esta memoria para dar respuestas consistentes. Si el usuario corrigió algo antes, respeta esa corrección. Si te enseñó un proceso, aplícalo.\n`
    : '';

  // Construir bloque de búsqueda web
  const webContext = webSearchResults
    ? `\nRESULTADOS DE BÚSQUEDA WEB (información actual de internet):
${webSearchResults}
IMPORTANTE: Usa esta información para complementar tu respuesta. Cita las fuentes cuando uses datos de la búsqueda web.\n`
    : '';

  const systemInstruction = `Eres el Super Agente IA de ${companyName || 'la empresa'} — un asistente inteligente con acceso a la base de conocimiento del negocio, memoria persistente y capacidad de búsqueda web.

${INTEGRITY_RULE}

CAPACIDADES:
1. CONOCIMIENTO PROFUNDO: Tienes acceso a ${allNotes.length} respuestas/snippets guardados de la empresa.
2. ANÁLISIS DE IMÁGENES: Puedes analizar capturas de pantalla, vouchers, chats de WhatsApp y cualquier imagen.
3. SOPORTE COMPLETO: Respondes preguntas de soporte técnico, ventas, información de productos/servicios, procesos internos.
4. GENERACIÓN DE CONTENIDO: Puedes redactar respuestas, mensajes, correos, propuestas comerciales.
5. ASESORÍA: Puedes asesorar sobre estrategias de venta, atención al cliente y procesos del negocio.
6. MEMORIA PERSISTENTE: Recuerdas información de conversaciones anteriores. Aprendes de correcciones, preferencias e instrucciones del usuario.
7. BÚSQUEDA WEB: Cuando necesitas información actualizada, puedes buscar en internet y citar fuentes.
8. WORDPRESS/WOOCOMMERCE/TUTOR LMS: Tienes conocimiento técnico profundo de estas plataformas.
${memoryBlock}
BASE DE CONOCIMIENTO — RESPUESTAS MÁS RELEVANTES (contenido completo):
${deepContext}
${exactDataSection}
${catalog ? `CATÁLOGO COMPLETO DE RESPUESTAS (${allNotes.length} total — solo títulos):\n${catalog}` : ''}
${companyContext}

${WP_WOOCOMMERCE_TUTOR_KNOWLEDGE}
${webContext}
DIRECTRICES:
- Usa la información de la base de conocimiento para dar respuestas PRECISAS y CONSISTENTES
- Si la pregunta coincide con una respuesta guardada, usa ESA información como base
- Si analizas una imagen, descríbela en contexto del negocio
- Mantén un tono profesional pero cercano y empático
- Responde en español
- Usa emojis apropiados cuando sea pertinente (sin exagerar)
- Si no tienes información suficiente, dilo honestamente y sugiere alternativas
- ${lengthDirective}${gestorFormatDirective}
- Cuando uses tu memoria persistente, puedes mencionar que recuerdas algo de una conversación anterior
- Cuando uses resultados de búsqueda web, cita las fuentes
${customPrompt ? `\nINSTRUCCIÓN PERSONALIZADA DEL USUARIO (PRIORITARIA, respétala por encima de las directrices anteriores salvo la REGLA CRÍTICA): ${customPrompt}` : ''}`;

  // Construir array de contents para multi-turno
  const contents = messages.map(msg => {
    const parts = [{ text: msg.content || '' }];
    // Agregar imagen inline si este mensaje la tiene
    if (msg.imageData) {
      try {
        const [header, data] = msg.imageData.split(',');
        const mimeType = header.match(/:(.*?);/)?.[1] || 'image/jpeg';
        parts.push({ inlineData: { mimeType, data } });
      } catch (e) { /* ignorar error de formato de imagen */ }
    }
    return {
      role: msg.role === 'assistant' ? 'model' : 'user',
      parts
    };
  });

  // Si hay imagen nueva en el último mensaje
  if (imagePreview && contents.length > 0) {
    const lastContent = contents[contents.length - 1];
    if (lastContent.role === 'user' && !messages[messages.length - 1]?.imageData) {
      try {
        const [header, data] = imagePreview.split(',');
        const mimeType = header.match(/:(.*?);/)?.[1] || 'image/jpeg';
        lastContent.parts.push({ inlineData: { mimeType, data } });
      } catch (e) { /* ignorar */ }
    }
  }

  // Hacer fetch directamente (no callGemini, que solo soporta single-turn)
  for (let i = 0; i < 3; i++) {
    try {
      const response = await fetch(`${GEMINI_URL}?key=${getGeminiKey()}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          contents,
          systemInstruction: { parts: [{ text: systemInstruction }] },
          // temperature baja: reduce que la IA "recree"/invente enlaces y datos
          generationConfig: { temperature: 0.25, maxOutputTokens }
        })
      });

      if (response.status === 429 || response.status === 500) {
        await new Promise(r => setTimeout(r, Math.pow(2, i) * 1000));
        continue;
      }
      if (!response.ok) throw new Error(`API error: ${response.status}`);

      const data = await response.json();
      if (data.candidates?.[0]?.content?.parts?.[0]?.text) {
        // Red de seguridad: corrige cualquier enlace inventado contra la base
        const raw = data.candidates[0].content.parts[0].text;
        const { text, replacements, suspicious } = sanitizeOutputLinks(raw, relevant);
        if (replacements.length > 0) {
          console.warn('IA: enlaces corregidos contra la base de conocimiento', replacements);
        }
        if (suspicious.length > 0) {
          console.warn('IA: enlaces sin coincidencia en la base (posible invención)', suspicious);
        }
        return text;
      }
      if (data.promptFeedback?.blockReason) {
        throw new Error(`Respuesta bloqueada: ${data.promptFeedback.blockReason}`);
      }
      throw new Error('No se recibió respuesta de Gemini');
    } catch (error) {
      if (i === 2) throw error;
    }
  }
  throw new Error('Se agotaron los reintentos');
};

/**
 * Refinar contenido con instrucción específica
 */
export const refineContent = async (content, instruction) => {
  const systemInstruction = `Eres un asistente de redacción experto.
Tu tarea es reescribir y mejorar el texto proporcionado siguiendo estrictamente la instrucción del usuario.

REGLAS ESTRICTAS:
- NO uses markdown (no uses **, ##, *, -, ni ningún símbolo de formato markdown)
- Devuelve SOLO texto plano limpio, sin formato markdown
- Si el texto original tiene estructura (títulos, subtítulos, listas), mantén la misma estructura usando solo texto plano
- NO agregues explicaciones, introducciones ni conclusiones ("Aquí tienes...", "Claro...")
- Devuelve ÚNICAMENTE el texto resultado, nada más
- Mantén el mismo idioma del texto original`;

  const prompt = `TEXTO ORIGINAL:\n"${content}"\n\nINSTRUCCIÓN:\n${instruction}`;

  return await callGemini(prompt, systemInstruction, null);
};

export const findRelevantSnippets = async (searchTerm, allNotes = [], maxResults = 15) => {
  // Convertir las notas a un formato simple para el prompt
  const notesContext = allNotes.map(note => ({
    id: note.id,
    title: note.title,
    category: note.category,
    content: note.content.substring(0, 250) // Solo un fragmento
  }));

  if (notesContext.length === 0) {
    return []; // No hay notas para buscar
  }

  const systemInstruction = `Eres un motor de búsqueda semántica ultra-preciso.
Tu tarea es analizar la CONSULTA de búsqueda del usuario y compararla con una LISTA DE NOTAS.
Debes devolver *únicamente* los IDs de las notas que sean más relevantes para la consulta.
La relevancia se basa en el *significado* y la *intención*, no solo en coincidencias de palabras clave.

Por ejemplo, si la consulta es "cómo facturo", una nota titulada "Emitir comprobante de pago" es relevante.

Responde *exclusivamente* con un array JSON de strings que contenga los IDs de las notas más relevantes (máximo 15).
Ejemplo de respuesta: ["id-abc-123", "id-xyz-789"]
Si no encuentras nada relevante, responde: []`;

  const prompt = `CONSULTA: "${searchTerm}"\n\nLISTA DE NOTAS (JSON):\n${JSON.stringify(notesContext)}`;

  let result = "[]"; // Default
  try {
    result = await callGemini(prompt, systemInstruction, null);
    // Intentar parsear el JSON que devuelve Gemini
    const ids = JSON.parse(result);
    if (Array.isArray(ids)) {
      return ids.slice(0, maxResults); // Limitar a maxResults
    }
    console.warn("Respuesta de IA no fue un array:", ids);
    return []; // Si no es un array, devuelve vacío
  } catch (error) {
    console.error("Error al parsear la respuesta de IA (findRelevantSnippets):", error, "Respuesta recibida:", result);
    return []; // Devolver vacío en caso de error
  }
};

/**
 * Analizar evidencia de venta (imagen de chat/voucher) verus reglas de comisión
 */
// Descarga una imagen y la convierte a Base64 (data URL)
const imageUrlToBase64 = async (imageUrl) => {
  const imageResponse = await fetch(imageUrl);
  const blob = await imageResponse.blob();
  return await new Promise((resolve) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.readAsDataURL(blob);
  });
};

// Bloque de criterio anti-error que comparten el análisis y el chat
const SALES_AUDIT_CRITERIA = `CRITERIO CLAVE — SEGUIMIENTO (lo más importante y donde más se equivoca la IA):
- "Seguimiento activo" SOLO cuenta si fue el ASESOR quien ESCRIBIÓ PRIMERO al cliente (mensaje iniciado por el vendedor) para retomar/empujar la venta.
- Responder a un cliente que escribió primero NO es seguimiento; es atención básica obligatoria.
- Analiza las FECHAS y la SECUENCIA: si pasan más de 24 horas (o varios días) y en ese hueco el ASESOR NO envió ningún mensaje iniciando contacto, y luego el CLIENTE vuelve por su cuenta y paga → NO hay comisión (Caso 8 / Caso 10).
- Pregúntate siempre: ¿quién rompió el silencio tras la pausa? Si fue el cliente → normalmente NO corresponde comisión.
- Solo hay comisión por seguimiento si el vendedor escribió primero tras la pausa (recordatorios, "¿sigue interesado?", reenvío de métodos de pago por iniciativa propia).
- Fíjate en los días/horas de cada burbuja para detectar pausas (ej. "9 de enero" ... "13 de enero").`;

export const analyzeSaleEvidence = async (imageUrl, rulesText, lessonsText = '') => {
  // 1. Descargar la imagen y convertir a Base64
  let base64Image = null;
  try {
    base64Image = await imageUrlToBase64(imageUrl);
  } catch (error) {
    console.error("Error descargando imagen para análisis:", error);
    throw new Error("No se pudo acceder a la imagen de evidencia.");
  }

  const lessonsBlock = lessonsText && lessonsText.trim()
    ? `\nLECCIONES APRENDIDAS (MÁXIMA PRIORIDAD — el administrador te corrigió antes, respétalas por encima de tu criterio):\n${lessonsText}\n`
    : '';

  const systemInstruction = `Eres un Auditor de Ventas experto y MUY ESTRICTO.
Tu tarea es analizar una captura de pantalla de un chat de WhatsApp y determinar si la venta es VÁLIDA para comisión según las reglas proporcionadas.
Ante la duda, tiende a marcar INVÁLIDA: la comisión solo se gana con gestión comercial activa comprobable.

Reglas de Comisión:
${rulesText}
${lessonsBlock}
${SALES_AUDIT_CRITERIA}

Pasos de Análisis:
1. Lee TODAS las marcas de tiempo y fechas del chat, en orden.
2. Identifica quién inició cada tramo de la conversación (Cliente o Vendedor).
3. Detecta pausas mayores a 24h y quién las rompió.
4. Determina si hubo SEGUIMIENTO real (asesor escribe primero) o solo atención (responde al cliente).
5. Busca confirmación de PAGO (voucher, "ya pagué", "Yapeaste", etc.).
6. Si la imagen NO es un chat/voucher o no es legible, marca isValid: false y reason: "Imagen no reconocida como evidencia de venta".

Además, OBSERVA la conversación como entrenador de ventas y extrae un aprendizaje para nuestro "Super Vendedor IA":
en "salesInsight" resume en 1-2 frases las técnicas comerciales útiles que se ven (cómo saluda, cómo maneja objeciones de precio/tiempo, cómo presenta promociones/métodos de pago, cómo cierra, tono). Si la venta NO se cerró o el chat no aporta, indica brevemente qué se pudo mejorar.

Formato de Respuesta (JSON estricto, sin texto adicional):
{
  "isValid": boolean,
  "confidence": "high" | "medium" | "low",
  "ruleApplied": "Caso X" (o "Regla General"),
  "reason": "Explicación breve y directa, citando quién hizo seguimiento y las fechas clave (máx 30 palabras).",
  "suggestedStatus": "active" | "rejected",
  "salesInsight": "Aprendizaje de venta de esta conversación (técnicas, objeciones, cierre, tono) en 1-2 frases."
}
`;

  const prompt = "Analiza esta evidencia de venta paso a paso (presta especial atención a las fechas y a quién hizo seguimiento). Responde solo con el JSON.";

  try {
    const resultText = await callGemini(prompt, systemInstruction, base64Image, 3, { temperature: 0.2 });
    // Limpiar bloques de código si existen
    const cleanJson = resultText.replace(/```json/g, '').replace(/```/g, '').trim();
    return JSON.parse(cleanJson);
  } catch (error) {
    console.error("Error en análisis de IA:", error);
    throw new Error("La IA no pudo analizar la imagen.");
  }
};

/**
 * Chat de revisión/enseñanza sobre una venta concreta.
 * El admin discute con la IA, la corrige y le enseña. La IA responde y,
 * cuando el admin establece una corrección/regla clara, propone guardar una lección
 * y/o actualizar el veredicto de esta venta.
 *
 * @param {string} imageUrl - evidencia de la venta
 * @param {Array} messages - [{ role: 'user'|'assistant', text }]
 * @param {Object} ctx - { rulesText, lessonsText, currentVerdict, saleInfo }
 * @returns {Object} { reply, suggestedVerdict: 'valid'|'invalid'|null, lessonToSave: string|null }
 */
export const chatSaleAnalysis = async (imageUrl, messages, ctx = {}) => {
  const { rulesText = '', lessonsText = '', currentVerdict = null, saleInfo = '' } = ctx;

  let base64Image = null;
  try {
    if (imageUrl) base64Image = await imageUrlToBase64(imageUrl);
  } catch (e) {
    // El chat puede seguir aunque la imagen falle
    console.warn('No se pudo cargar la imagen para el chat IA:', e);
  }

  const lessonsBlock = lessonsText && lessonsText.trim()
    ? `\nLECCIONES YA APRENDIDAS:\n${lessonsText}\n`
    : '';

  const systemInstruction = `Eres el Auditor de Ventas IA conversando con el ADMINISTRADOR sobre UNA venta específica para revisarla y APRENDER de él.
El administrador es la máxima autoridad: si te corrige, ACEPTA la corrección y agradece, no discutas de forma terca (puedes pedir aclaración si algo es ambiguo).

Venta en revisión: ${saleInfo || 'N/D'}
Veredicto actual del sistema: ${currentVerdict === true ? 'VÁLIDA' : currentVerdict === false ? 'INVÁLIDA' : 'sin veredicto'}

Reglas de Comisión:
${rulesText}
${lessonsBlock}
${SALES_AUDIT_CRITERIA}

Tu trabajo en cada turno:
1. Responde de forma BREVE, clara y conversacional (en español, máx 4 frases).
2. Si el administrador deja claro el veredicto correcto de ESTA venta, refléjalo en "suggestedVerdict" ('valid' o 'invalid'); si no toca el veredicto, déjalo en null.
3. Si el administrador te ENSEÑA una regla o criterio GENERAL reutilizable (no solo este caso), redáctalo como una lección corta, clara y accionable en "lessonToSave" (1-2 frases, redactada para aplicarla a futuras ventas). Si en este turno no hay una lección nueva que valga la pena guardar, deja "lessonToSave" en null.
4. No guardes lecciones triviales ni dupliques las ya aprendidas.

Responde SIEMPRE en JSON estricto, sin texto fuera del JSON:
{
  "reply": "tu respuesta conversacional al admin",
  "suggestedVerdict": "valid" | "invalid" | null,
  "lessonToSave": "lección general a recordar" | null
}`;

  // Construir el prompt con el historial
  const history = messages.map(m => `${m.role === 'user' ? 'ADMIN' : 'IA'}: ${m.text}`).join('\n');
  const prompt = `Historial de la conversación:\n${history}\n\nResponde al último mensaje del ADMIN. Solo el JSON.`;

  try {
    const resultText = await callGemini(prompt, systemInstruction, base64Image, 3, { temperature: 0.4 });
    const cleanJson = resultText.replace(/```json/g, '').replace(/```/g, '').trim();
    const parsed = JSON.parse(cleanJson);
    return {
      reply: parsed.reply || 'Entendido.',
      suggestedVerdict: parsed.suggestedVerdict === 'valid' ? 'valid' : parsed.suggestedVerdict === 'invalid' ? 'invalid' : null,
      lessonToSave: parsed.lessonToSave && String(parsed.lessonToSave).trim() ? String(parsed.lessonToSave).trim() : null
    };
  } catch (error) {
    console.error("Error en chat de análisis IA:", error);
    throw new Error("La IA no pudo procesar el mensaje.");
  }
};

/**
 * SUPER VENDEDOR IA — chat de simulación de ventas (para futuro WhatsApp).
 * Aprende de los insights extraídos de ventas reales.
 *
 * @param {Array} messages - [{ role: 'user'|'assistant', text }]
 * @param {Object} ctx - { mode: 'sell'|'customer', knowledgeText, companyName }
 *   - mode 'sell':    la IA es el VENDEDOR y le vende al usuario (cliente).
 *   - mode 'customer': la IA es un CLIENTE con objeciones y el usuario practica vender.
 * @returns {string} respuesta de la IA
 */
export const superVendedorChat = async (messages, ctx = {}) => {
  const { mode = 'sell', knowledgeText = '', companyName = 'Hingenia' } = ctx;

  const knowledgeBlock = knowledgeText && knowledgeText.trim()
    ? `\nLO QUE HAS APRENDIDO DE VENTAS REALES (úsalo a tu favor):\n${knowledgeText}\n`
    : '';

  const base = `Trabajas para ${companyName}, una academia de educación online para ingeniería y construcción (cursos en vivo y grabados: AutoCAD, ETABS, Revit, metrados, costos y presupuestos, estructuras, etc.). El canal es un chat estilo WhatsApp, en español peruano, cercano y con emojis con moderación. Precios en soles (S/).`;

  const systemInstruction = mode === 'customer'
    ? `${base}
ROL: Eres un CLIENTE POTENCIAL realista (a veces indeciso, con objeciones de precio/tiempo/utilidad). El usuario es el VENDEDOR que practica venderte un curso.
- Pon objeciones razonables y deja que te convenzan; si lo hacen bien, avanza hacia la compra.
- Mantén respuestas cortas y naturales como en WhatsApp.
- Cuando la conversación llegue a un cierre (compras o te retiras), agrega al final una línea separada que empiece con "💡 FEEDBACK:" con 1-2 consejos para el vendedor.
${knowledgeBlock}`
    : `${base}
ROL: Eres el SUPER VENDEDOR de ${companyName}. El usuario es un CLIENTE. Tu meta es asesorar y CERRAR la venta con calidez y técnica.
- Saluda, detecta la necesidad, presenta el curso y beneficios, maneja objeciones (precio, tiempo, dudas), ofrece promociones y métodos de pago, y guía al cierre.
- Respuestas cortas y naturales como en WhatsApp (no párrafos largos). Usa los métodos de pago y promos al estilo de las ventas reales aprendidas.
- No inventes precios exactos si no los sabes; habla de rangos/promos y pide el dato o deriva al pago.
${knowledgeBlock}`;

  const history = messages.map(m => `${m.role === 'user' ? 'USUARIO' : 'IA'}: ${m.text}`).join('\n');
  const prompt = `Conversación hasta ahora:\n${history}\n\nResponde al último mensaje del USUARIO en el rol indicado. Solo tu respuesta, sin prefijos.`;

  try {
    const resultText = await callGemini(prompt, systemInstruction, null, 3, { temperature: 0.8 });
    return resultText.trim();
  } catch (error) {
    console.error("Error en Super Vendedor IA:", error);
    throw new Error("El Super Vendedor no pudo responder.");
  }
};

/**
 * --- FUNCIÓN MEJORADA PARA CALENDARIO CON IA ESTRATÉGICA ---
 * Generar un horario de clases basado en instructores, cursos, asesores y reglas de negocio avanzadas
 */
export const generateSchedule = async (instructors, courses, month, year, preferences = {}, googleCalendarEvents = []) => {
  // Fecha actual
  const today = new Date();
  const todayDay = today.getDate();
  const todayMonth = today.getMonth() + 1;
  const todayYear = today.getFullYear();

  // Calcular el día de inicio real
  // Si el mes/año es el actual o pasado, empezar desde HOY + 1 día mínimo
  // Si el mes es futuro, empezar desde el primer día hábil del mes
  let startDay;
  const isCurrentOrPastMonth = (year < todayYear) || (year === todayYear && month <= todayMonth);

  if (isCurrentOrPastMonth && month === todayMonth && year === todayYear) {
    // Mes actual: dar tiempo para publicidad y preparación (mínimo 5 días)
    startDay = todayDay + 5; // Mínimo 5 días de preparación para publicidad

    // Asegurarse de que sea día hábil
    const checkDate = new Date(year, month - 1, startDay);
    while (checkDate.getDay() === 0 || checkDate.getDay() === 6) {
      checkDate.setDate(checkDate.getDate() + 1);
      startDay = checkDate.getDate();
    }
  } else if (isCurrentOrPastMonth && month < todayMonth) {
    // Mes pasado: no tiene sentido, usar día 1 del siguiente mes
    startDay = 1;
  } else {
    // Mes futuro: usar primer día hábil
    const firstDay = new Date(year, month - 1, 1);
    startDay = 1;
    while (firstDay.getDay() === 0 || firstDay.getDay() === 6) {
      firstDay.setDate(firstDay.getDate() + 1);
      startDay = firstDay.getDate();
    }
  }

  // Usar masterclassCount de preferences o default 6
  const minMasterclassPorCurso = preferences.masterclassCount || 6;

  // Calcular días restantes en el mes
  const daysInMonth = new Date(year, month, 0).getDate();

  // Safeguard: si startDay excede el mes, ajustar al siguiente mes
  if (startDay > daysInMonth) {
    console.warn(`startDay (${startDay}) excede días del mes (${daysInMonth}). Ajustando al próximo mes.`);
    // En este caso, deberíamos programar en el siguiente mes
    // Por ahora, usar el último día disponible pero advertir
    startDay = daysInMonth;
  }

  const diasDisponibles = daysInMonth - startDay + 1;

  const systemInstruction = `Eres un CEREBRO OPERATIVO AGRESIVO de Planificación Comercial.
Tu ÚNICA misión es MAXIMIZAR VENTAS programando masterclass lo MÁS PRONTO POSIBLE.

=== ⚠️ REGLA ABSOLUTA: PROHIBIDO PROGRAMAR EN EL PASADO ===
HOY es ${todayYear}-${String(todayMonth).padStart(2,'0')}-${String(todayDay).padStart(2,'0')}.
El día MÍNIMO permitido es ${startDay} (hoy + 5 días para publicidad).
NUNCA generes fechas ANTES del día ${startDay}. Esto es CRÍTICO.

=== REGLA CRÍTICA #1: URGENCIA TEMPORAL ===
LAS MASTERCLASS DEBEN EMPEZAR EL DÍA ${startDay} DEL MES.
NO ESPERES. NO DEJES DÍAS VACÍOS DESPUÉS DEL DÍA ${startDay}.
La 1M de cada curso DEBE ser entre el día ${startDay} y ${startDay + 3}.

=== REGLA CRÍTICA #2: CANTIDAD REQUERIDA ===
CADA CURSO necesita EXACTAMENTE ${minMasterclassPorCurso} masterclass.

=== ⚠️ REGLA CRÍTICA #3: SEPARACIÓN OBLIGATORIA DE 5 DÍAS ===
Entre cada masterclass del MISMO curso, DEBE haber MÍNIMO 5 DÍAS de separación.
Esto es para dar tiempo de publicidad entre cada masterclass.
Ejemplo correcto:
- 1M el día 9
- 2M el día 14 (5 días después)
- 3M el día 19 (5 días después)
- 4M el día 24 (5 días después)
INCORRECTO: 1M día 9, 2M día 11 (solo 2 días - PROHIBIDO)

=== REGLA CRÍTICA #4: OPTIMIZACIÓN DE SLOTS ===
PUEDES programar 2-3 masterclass de DIFERENTES cursos el mismo día en horarios distintos.
Ejemplos válidos:
- 6pm: 1M - Curso A
- 7pm: 1M - Curso B (diferente curso, mismo día = OK)

=== REGLAS DE INSTRUCTOR ===
- NO programar si estado = "saturado" o "descanso"
- Respetar maxEventosDia (máximo eventos por día por instructor)
- NO superponer horarios del mismo instructor

=== HORARIOS ===
Masterclass: 18:00, 19:00, 20:00 o 21:00 (horario prime)
Duración típica: 1-3 horas según configuración del curso

=== NOMENCLATURA ===
Masterclass: "1M - [Curso]", "2M - [Curso]", "3M - [Curso]"...
Clases: "Sesión 1 - [Curso]", "Sesión 2 - [Curso]"...

=== COLORES POR CURSO ===
CADA curso tiene asignado un color ÚNICO. USA EL COLOR DEL CURSO en cada evento.
Todos los eventos del mismo curso DEBEN tener el MISMO color.

${googleCalendarEvents.length > 0 ? `=== EVENTOS DE GOOGLE CALENDAR (BLOQUEOS EXTERNOS) ===
El usuario tiene los siguientes eventos en su Google Calendar personal.
DEBES EVITAR programar masterclass que se solapen con estos horarios.
Trátalos como bloques de tiempo NO DISPONIBLES.
Si un slot tiene un evento de Google Calendar, usa otro horario del mismo día o mueve al siguiente día disponible.

${JSON.stringify(googleCalendarEvents, null, 2)}

` : ''}=== FORMATO JSON ESTRICTO (SIN MARKDOWN) ===
[{"title":"1M - NombreCurso","start":"${year}-${String(month).padStart(2,'0')}-${String(startDay).padStart(2,'0')}T19:00:00","end":"${year}-${String(month).padStart(2,'0')}-${String(startDay).padStart(2,'0')}T22:00:00","instructorId":"id-del-instructor","courseId":"id-del-curso","color":"#COLOR-DEL-CURSO","type":"masterclass"}]

IMPORTANTE:
- Genera EXACTAMENTE ${minMasterclassPorCurso} masterclass por CADA curso.
- USA el color que viene en los datos del curso.
- MÍNIMO 5 días entre MC del mismo curso.
`;

  // Preparar datos enriquecidos para la IA
  const instructoresData = instructors.map(i => ({
    id: i.id,
    name: i.name,
    color: i.color,
    estado: i.estado || 'activo',
    maxEventosDia: i.maxEventosDia || 3,
    diasBloqueados: i.diasBloqueados || []
  }));

  // Paleta de colores únicos para cada curso (bien diferenciados)
  const courseColors = [
    '#8b5cf6', // Violeta
    '#10b981', // Verde esmeralda
    '#f59e0b', // Naranja/Ámbar
    '#ef4444', // Rojo
    '#3b82f6', // Azul
    '#ec4899', // Rosa
    '#06b6d4', // Cian
    '#84cc16', // Lima
    '#f97316', // Naranja intenso
    '#6366f1', // Índigo
  ];

  const cursosData = courses.map((c, index) => ({
    id: c.id,
    name: c.name,
    instructorId: c.instructorId,
    masterclassHours: c.masterclassHours || 3,
    masterclassRequeridas: minMasterclassPorCurso,
    color: c.color || courseColors[index % courseColors.length] // Asignar color único
  }));

  const prompt = `
=== GENERAR ${minMasterclassPorCurso} MASTERCLASS POR CURSO PARA ${month}/${year} ===

PRIMER DÍA HÁBIL: ${year}-${String(month).padStart(2,'0')}-${String(startDay).padStart(2,'0')}

=== INSTRUCTORES ===
${JSON.stringify(instructoresData, null, 2)}

=== CURSOS (${courses.length} cursos × ${minMasterclassPorCurso} MC = ${minMasterclassPorCurso * courses.length} eventos) ===
${JSON.stringify(cursosData, null, 2)}

=== LÍMITES ===
- Max masterclass/día (global): ${preferences.maxMasterclassDiario || 5}
- Horarios válidos: 18:00, 19:00, 20:00, 21:00

=== TAREA ===
Genera EXACTAMENTE ${minMasterclassPorCurso} masterclass para CADA uno de los ${courses.length} cursos.
Total de eventos esperados: ${minMasterclassPorCurso * courses.length}

La 1M de cada curso debe ser entre el día ${startDay} y ${startDay + 3}.
⚠️ CRÍTICO: Separa las MC del MISMO curso por MÍNIMO 5 DÍAS.
Puedes poner varias MC de DIFERENTES cursos el mismo día.

Responde SOLO con el JSON array, sin texto adicional ni bloques de código.
`;

  try {
    const resultText = await callGemini(prompt, systemInstruction, null);

    // 4. Limpieza agresiva de Markdown y texto extra
    let cleanJson = resultText.replace(/```json/g, '').replace(/```/g, '').trim();

    // Si la IA responde con texto antes o después del JSON, intentar extraerlo con regex
    const jsonMatch = cleanJson.match(/\[[\s\S]*\]/);
    if (jsonMatch) {
      cleanJson = jsonMatch[0];
    }

    // 5. Intento de reparación de JSON truncado (Avanzado)
    if (!cleanJson.endsWith(']')) {
      console.warn("JSON truncado detectado. Intentando reparar...");

      // 1. Balancear comillas (Si hay impar, falta una al final)
      const quoteCount = (cleanJson.match(/"/g) || []).length;
      if (quoteCount % 2 !== 0) {
        cleanJson += '"';
      }

      // 2. Balancear llaves/corchetes (muy simplificado, asumiendo estructura plana de array de objetos)
      // Si lo último es una comilla (recién agregada o no) o un valor, probablemente falta cerrar objeto
      // Verificamos si la última llave abierta no tiene cierre
      const openBraces = (cleanJson.match(/\{/g) || []).length;
      const closeBraces = (cleanJson.match(/\}/g) || []).length;
      if (openBraces > closeBraces) {
        cleanJson += '}';
      }

      // 3. Cerrar Array
      if (!cleanJson.endsWith(']')) {
        cleanJson += ']';
      }
    }

    try {
      return JSON.parse(cleanJson);
    } catch (parseError) {
      console.error("Fallo el primer intento de parseo. Texto:", cleanJson);
      throw new Error(`Error de sintaxis JSON: ${parseError.message}`);
    }

  } catch (error) {
    console.error("Error generando horario con IA:", error);
    // Add more context to the error
    throw new Error(`Error de IA: ${error.message}`);
  }
};

/**
 * Mejorar plantillas de mensajes con IA
 * Escanea TODAS las plantillas para contexto, mejora títulos y contenido
 * SIN tocar las variables dinámicas {{variable}}
 */
export const improveTemplatesWithAI = async (templates, courseName) => {
  const systemInstruction = `Eres un experto en copywriting y marketing digital para cursos online y masterclass.
Tu tarea es MEJORAR los mensajes de seguimiento de una academia, haciéndolos más persuasivos, profesionales y atractivos.

=== REGLAS ABSOLUTAS ===
1. NUNCA modifiques las variables dinámicas: {{zoomLink}}, {{whatsappLink}}, {{time}}, {{meetingId}}, {{password}} o cualquier texto entre {{ }}. Déjalas EXACTAMENTE como están.
2. RESPETA la estructura temporal de cada mensaje (si dice "30 minutos antes", "2 horas antes", "es hoy", etc. - mantén ese contexto).
3. MEJORA los títulos para que sean más llamativos y persuasivos.
4. MEJORA el contenido principal haciéndolo más profesional, con mejor copywriting, emojis estratégicos y llamados a la acción.
5. MANTÉN el mismo idioma (español).
6. NO agregues variables nuevas que no existan en el original.
7. Cada mensaje debe sentirse único, no repetitivo respecto a los demás.
8. Los mensajes deben generar URGENCIA y EMOCIÓN para que asistan a la masterclass.

=== FORMATO DE RESPUESTA ===
Responde SOLO con un JSON array, sin texto adicional ni bloques de código markdown.
Cada elemento debe tener exactamente: {"id": "id-original", "title": "título mejorado", "content": "contenido mejorado"}
IMPORTANTE: Usa el MISMO id del template original para poder mapear las mejoras.`;

  const templatesData = templates.map(t => ({
    id: t.id,
    title: t.title,
    content: t.content,
    category: t.category || 'General'
  }));

  const prompt = `
=== CURSO: "${courseName}" ===

=== PLANTILLAS ACTUALES (${templates.length} mensajes) ===
${JSON.stringify(templatesData, null, 2)}

=== TAREA ===
Mejora TODAS las ${templates.length} plantillas anteriores.
- Haz los títulos más llamativos y persuasivos
- Mejora el copywriting del contenido
- Agrega emojis estratégicos donde tenga sentido
- Mantén las variables {{...}} INTACTAS
- Respeta la estructura temporal (30 min, 2 horas, "es hoy", etc.)
- Cada mensaje debe sentirse único

Responde SOLO con el JSON array. Sin markdown, sin texto extra.`;

  try {
    const resultText = await callGemini(prompt, systemInstruction, null);

    let cleanJson = resultText.replace(/```json/g, '').replace(/```/g, '').trim();
    const jsonMatch = cleanJson.match(/\[[\s\S]*\]/);
    if (jsonMatch) {
      cleanJson = jsonMatch[0];
    }

    // Reparar JSON truncado
    if (!cleanJson.endsWith(']')) {
      const quoteCount = (cleanJson.match(/"/g) || []).length;
      if (quoteCount % 2 !== 0) cleanJson += '"';
      const openBraces = (cleanJson.match(/\{/g) || []).length;
      const closeBraces = (cleanJson.match(/\}/g) || []).length;
      if (openBraces > closeBraces) cleanJson += '}';
      if (!cleanJson.endsWith(']')) cleanJson += ']';
    }

    return JSON.parse(cleanJson);
  } catch (error) {
    console.error("Error mejorando plantillas con IA:", error);
    throw new Error(`Error de IA: ${error.message}`);
  }
};

/**
 * Analizar patrones del Google Calendar del usuario con IA
 * Detecta cursos, masterclasses, sesiones y patrones de programación
 * @param {Array} events - Eventos de Google Calendar
 * @returns {Object} Análisis detallado por curso y patrones generales
 */
export const analyzeCalendarPatterns = async (events) => {
  if (!events || events.length === 0) {
    throw new Error('No hay eventos para analizar');
  }

  // Limitar a máximo 200 eventos en formato ultra compacto
  const MAX_EVENTS = 200;
  const eventsToAnalyze = events.length > MAX_EVENTS
    ? events.slice(-MAX_EVENTS)
    : events;

  // Formato ultra compacto: "TITULO|YYYY-MM-DD|HH:MM" por línea (ahorra ~70% de tokens)
  const eventsCompact = eventsToAnalyze
    .map(e => {
      const titulo = (e.title || e.summary || '').trim();
      const start = e.start instanceof Date ? e.start : new Date(e.start);
      if (!titulo || isNaN(start.getTime())) return null;
      const fecha = start.toISOString().slice(0, 10);
      const hora = start.toISOString().slice(11, 16);
      return `${titulo}|${fecha}|${hora}`;
    })
    .filter(Boolean)
    .join('\n');

  const systemInstruction = `Eres un analista experto en academias de educación online.
Recibirás eventos de Google Calendar en formato: TITULO|FECHA|HORA_UTC

CONVENCIÓN DE NOMBRES de esta academia:
- "1M INCENDIO" o "1M - INCENDIO" = Masterclass número 1 del curso INCENDIO
- "2M PHOTOSHOP" = Masterclass número 2 del curso PHOTOSHOP
- "1 SESION INDEPENDIZACION" o "SESION 1 INDEPENDIZACION" = Sesión 1 del curso
- "EXTRA MASTER INCENDIO" o "Master Extra INCENDIO" = Masterclass extra del curso
- "RECUPERACION INCENDIO" = Clase de recuperación
- El nombre del instructor puede aparecer al final: "1M INCENDIO angel HG2"
- Prefijos de hora: "6pm", "7pm", "8pm" al inicio del título indican el horario

IMPORTANTE: Las horas en el calendario están en UTC-5 (Perú). Suma 5 horas a los valores UTC para obtener la hora real.

Tu tarea: identificar cada curso por su nombre base (ignorando números y palabras como SESION/MASTER/EXTRA), y para cada curso detectar el patrón de programación.

RESPONDE ÚNICAMENTE con este JSON exacto (sin markdown, sin texto extra):
{
  "resumen": "2-3 oraciones describiendo los patrones más importantes del calendario",
  "estadisticas": {
    "totalEventos": número,
    "cursosDetectados": número,
    "diasConMasCarga": ["Lunes","Martes"],
    "horariosPopulares": ["6pm","7pm"],
    "promedioEventosPorSemana": número
  },
  "cursos": [
    {
      "nombre": "NOMBRE DEL CURSO",
      "masterclassActual": número (último número M detectado, ej: si hay 9M el valor es 9),
      "sesionActual": número (último número de SESION detectado),
      "totalEventos": número,
      "diasHabituales": "ej: Miércoles y Viernes",
      "horaHabitual": "ej: 7-8pm hora Perú",
      "frecuenciaDias": número (cada cuántos días aproximadamente hay un evento),
      "fechaInicio": "YYYY-MM-DD",
      "fechaUltimoEvento": "YYYY-MM-DD",
      "estado": "activo|finalizado|pausado"
    }
  ],
  "patrones": [
    {
      "tipo": "horario|carga|frecuencia|curso",
      "descripcion": "Patrón específico encontrado con datos concretos",
      "impacto": "alto|medio|bajo"
    }
  ],
  "recomendaciones": [
    {
      "titulo": "Acción concreta recomendada",
      "detalle": "Por qué hacerlo y cómo impacta la operación",
      "prioridad": "alta|media|baja"
    }
  ]
}`;

  const prompt = `Analiza estos ${eventsCompact.split('\n').length} eventos de calendario${events.length > MAX_EVENTS ? ` (últimos ${MAX_EVENTS} de ${events.length} totales)` : ''}.

Formato: TITULO|FECHA|HORA_UTC
${eventsCompact}

Identifica TODOS los cursos únicos, su avance actual (número de masterclass y sesión), su horario habitual y frecuencia. Genera patrones y recomendaciones basadas en los datos reales.`;

  try {
    const resultText = await callGemini(prompt, systemInstruction, null);

    // Limpiar bloques markdown
    let raw = resultText.replace(/```json\s*/gi, '').replace(/```\s*/g, '').trim();

    // Extraer solo el objeto JSON principal
    const jsonMatch = raw.match(/\{[\s\S]*\}/);
    if (jsonMatch) raw = jsonMatch[0];

    // Helper de reparación progresiva
    const tryFixes = [
      (s) => s,                                                        // 1: sin cambios
      (s) => s.replace(/,\s*([}\]])/g, '$1'),                         // 2: quitar trailing commas
      (s) => s.replace(/[\x00-\x1f\x7f]/g, ' '),                     // 3: quitar control chars
      (s) => s.replace(/[\x00-\x1f\x7f]/g, ' ')                      // 4: ambas
                .replace(/,\s*([}\]])/g, '$1'),
    ];

    for (const fix of tryFixes) {
      try { return JSON.parse(fix(raw)); } catch { /* siguiente */ }
    }

    // Fallback final: extraer resumen con regex y devolver estructura mínima
    const resumenMatch = raw.match(/"resumen"\s*:\s*"((?:[^"\\]|\\.)*)"/);
    const resumen = resumenMatch ? resumenMatch[1] : 'Análisis completado (ver consola para detalles).';
    console.warn('analyzeCalendarPatterns: JSON inválido de Gemini, usando fallback.\nRaw:', raw.slice(0, 500));
    return {
      resumen,
      estadisticas: { totalEventos: eventsToAnalyze.length, cursosDetectados: 0, diasConMasCarga: [], horariosPopulares: [] },
      cursos: [], patrones: [], recomendaciones: [],
    };
  } catch (error) {
    console.error('Error analizando calendario con IA:', error);
    throw new Error(`Error de análisis: ${error.message}`);
  }
};

/**
 * Analizar patrones con MEMORIA: compara el estado actual con análisis previos
 * @param {Array} events - Eventos del calendario
 * @param {Object|null} previousAnalysis - Último análisis guardado (o null)
 * @returns {Object} Análisis enriquecido con campo `cambios` comparando con el anterior
 */
export const analyzeCalendarWithMemory = async (events, previousAnalysis = null) => {
  if (!events || events.length === 0) throw new Error('No hay eventos para analizar');

  const MAX_EVENTS = 200;
  const eventsToAnalyze = events.length > MAX_EVENTS ? events.slice(-MAX_EVENTS) : events;

  const eventsCompact = eventsToAnalyze
    .map(e => {
      const titulo = (e.title || e.summary || '').trim();
      const start = e.start instanceof Date ? e.start : new Date(e.start);
      if (!titulo || isNaN(start.getTime())) return null;
      return `${titulo}|${start.toISOString().slice(0, 10)}|${start.toISOString().slice(11, 16)}`;
    })
    .filter(Boolean)
    .join('\n');

  // Contexto del análisis previo para darle memoria a la IA
  const memoryContext = previousAnalysis
    ? `\nANÁLISIS PREVIO (guardado el ${previousAnalysis.fechaAnalisis}):
Resumen anterior: ${previousAnalysis.resumen}
Estado de cursos en ese momento:
${(previousAnalysis.cursos || []).map(c =>
  `- ${c.nombre}: ${c.masterclassActual ? c.masterclassActual + 'M' : ''}${c.sesionActual ? ' Sesión ' + c.sesionActual : ''} | ${c.estado || 'activo'}`
).join('\n')}

IMPORTANTE: Compara el estado actual con el previo y genera el campo "cambios" con lo que avanzó o cambió.`
    : '';

  const systemInstruction = `Eres un analista experto en academias de educación online con MEMORIA de análisis previos.
Recibirás eventos de Google Calendar en formato: TITULO|FECHA|HORA_UTC

CONVENCIÓN DE NOMBRES de esta academia:
- "1M INCENDIO" o "1M - INCENDIO" = Masterclass número 1 del curso INCENDIO
- "2M PHOTOSHOP" = Masterclass número 2
- "1 SESION INDEPENDIZACION" = Sesión 1 del curso
- "EXTRA MASTER INCENDIO" = Masterclass extra
- "RECUPERACION INCENDIO" = Clase de recuperación
- Instructor puede aparecer al final: "1M INCENDIO angel HG2"
- Las horas UTC hay que sumarle 5 para obtener hora Perú (UTC-5)

RESPONDE ÚNICAMENTE con este JSON exacto (sin markdown, sin texto extra, sin trailing commas):
{
  "resumen": "2-3 oraciones sobre el estado actual y cambios respecto al análisis anterior",
  "estadisticas": {
    "totalEventos": number,
    "cursosDetectados": number,
    "cursosActivos": number,
    "diasConMasCarga": ["Lunes","Miercoles"],
    "horariosPopulares": ["6pm","7pm"],
    "promedioEventosPorSemana": number
  },
  "cursos": [
    {
      "nombre": "NOMBRE CURSO",
      "masterclassActual": number,
      "sesionActual": number,
      "totalEventos": number,
      "diasHabituales": "Miercoles y Viernes",
      "horaHabitual": "7-8pm hora Peru",
      "frecuenciaDias": number,
      "fechaInicio": "YYYY-MM-DD",
      "fechaUltimoEvento": "YYYY-MM-DD",
      "estado": "activo"
    }
  ],
  "cambios": [
    {
      "curso": "NOMBRE CURSO",
      "descripcion": "Avanzó de 7M a 9M en masterclasses",
      "tipo": "avance"
    }
  ],
  "patrones": [
    {
      "tipo": "horario",
      "descripcion": "Descripcion concreta con datos",
      "impacto": "alto"
    }
  ],
  "recomendaciones": [
    {
      "titulo": "Accion concreta",
      "detalle": "Por que y como",
      "prioridad": "alta"
    }
  ]
}`;

  const prompt = `Analiza estos ${eventsCompact.split('\n').length} eventos de calendario${events.length > MAX_EVENTS ? ` (últimos ${MAX_EVENTS} de ${events.length} totales)` : ''}.${memoryContext}

Formato: TITULO|FECHA|HORA_UTC
${eventsCompact}

Identifica TODOS los cursos, su masterclass y sesión actual, horario y frecuencia. ${previousAnalysis ? 'Genera el campo "cambios" comparando con el análisis previo.' : 'El campo "cambios" debe estar vacío [].'} Responde SOLO con el JSON.`;

  try {
    const resultText = await callGemini(prompt, systemInstruction, null);
    let raw = resultText.replace(/```json\s*/gi, '').replace(/```\s*/g, '').trim();
    const jsonMatch = raw.match(/\{[\s\S]*\}/);
    if (jsonMatch) raw = jsonMatch[0];

    const tryFixes = [
      (s) => s,
      (s) => s.replace(/,\s*([}\]])/g, '$1'),
      (s) => s.replace(/[\x00-\x1f\x7f]/g, ' '),
      (s) => s.replace(/[\x00-\x1f\x7f]/g, ' ').replace(/,\s*([}\]])/g, '$1'),
    ];

    for (const fix of tryFixes) {
      try { return JSON.parse(fix(raw)); } catch { /* siguiente */ }
    }

    const resumenMatch = raw.match(/"resumen"\s*:\s*"((?:[^"\\]|\\.)*)"/);
    return {
      resumen: resumenMatch ? resumenMatch[1] : 'Análisis completado.',
      estadisticas: { totalEventos: eventsToAnalyze.length, cursosDetectados: 0, cursosActivos: 0, diasConMasCarga: [], horariosPopulares: [] },
      cursos: [], cambios: [], patrones: [], recomendaciones: [],
    };
  } catch (error) {
    console.error('Error en analyzeCalendarWithMemory:', error);
    throw new Error(`Error de análisis: ${error.message}`);
  }
};

/**
 * Chat de programación con IA — propone horarios, detecta cruces, entiende el calendario
 * @param {string} userMessage - Mensaje del usuario
 * @param {Array} chatHistory - Historial [{role:'user'|'ai', content:string}]
 * @param {Object} calendarContext - { events, instructors, courses, savedMemory }
 * @returns {Object} { respuesta, eventos, conflictos, advertencias }
 */
export const chatWithCalendarAI = async (userMessage, chatHistory = [], calendarContext = {}) => {
  const { events = [], instructors = [], courses = [], savedMemory = null } = calendarContext;

  // Resumir eventos actuales (últimos 60 días + próximos 60 días) para el contexto
  const now = new Date();
  const pastLimit = new Date(now.getTime() - 60 * 24 * 3600 * 1000);
  const futureLimit = new Date(now.getTime() + 60 * 24 * 3600 * 1000);
  const relevantEvents = events
    .filter(e => {
      const s = e.start instanceof Date ? e.start : new Date(e.start);
      return s >= pastLimit && s <= futureLimit;
    })
    .slice(-120)
    .map(e => {
      const s = e.start instanceof Date ? e.start : new Date(e.start);
      return `${(e.title || '').slice(0, 40)}|${s.toISOString().slice(0, 16)}`;
    })
    .join('\n');

  // Estado de cursos desde la memoria
  const memoryContext = savedMemory
    ? `\nESTADO ACTUAL DE CURSOS (memoria del ${savedMemory.fechaAnalisis}):\n` +
      (savedMemory.cursos || []).map(c =>
        `- ${c.nombre}: ${c.masterclassActual ? c.masterclassActual + 'M' : '?M'} | Sesión ${c.sesionActual || '?'} | ${c.estado || 'activo'}`
      ).join('\n')
    : '';

  // Instructores y cursos disponibles
  const instructorsList = instructors.length > 0
    ? instructors.map(i => `${i.name}${i.color ? '' : ''}`).join(', ')
    : 'No hay instructores registrados';
  const coursesList = courses.length > 0
    ? courses.map(c => c.name).join(', ')
    : 'No hay cursos registrados';

  const systemInstruction = `Eres un asistente de programación académica inteligente para una academia online de diseño y arquitectura.

CONTEXTO DEL CALENDARIO:
Fecha actual: ${now.toLocaleDateString('es-PE', { weekday:'long', year:'numeric', month:'long', day:'numeric' })}
Instructores: ${instructorsList}
Cursos: ${coursesList}${memoryContext}

EVENTOS RECIENTES Y PRÓXIMOS (formato TITULO|FECHA_HORA):
${relevantEvents || '(sin eventos próximos)'}

REGLAS DE PROGRAMACIÓN DE LA ACADEMIA:
- Formato de eventos: "XM CURSO instructor" = Masterclass X del CURSO con ese instructor
- "X SESION CURSO" o "SESION X CURSO" = Sesión X del CURSO
- Horarios habituales: 6pm, 7pm, 8pm hora Perú (UTC-5 = suma +5h al UTC)
- Hay 6 asesores que programan sesiones individuales con alumnos DESPUÉS de cada masterclass
- Las sesiones van en horarios distintos a la masterclass del mismo día
- DETECCIÓN DE CRUCES: un instructor no puede estar en dos eventos simultáneos
- Una masterclass nueva sigue la numeración actual (si hay 9M, la siguiente es 10M)
- Los cursos tienen ~2 eventos por semana (masterclass + sesión)

FORMATO DE RESPUESTA:
Responde siempre en español claro y directo.
Si propones eventos para crear, incluye AL FINAL de tu respuesta este bloque exacto:
[EVENTOS]
[{"titulo":"5M INCENDIO angel HG1","inicio":"2026-02-23T18:00:00","fin":"2026-02-23T19:00:00","tipo":"masterclass","color":"#6366f1"},{"titulo":"1 SESION INCENDIO","inicio":"2026-02-23T19:00:00","fin":"2026-02-23T20:00:00","tipo":"sesion","color":"#3b82f6"}]
[/EVENTOS]
Si hay conflictos incluye:
[CONFLICTOS]
["Descripción del conflicto 1","Descripción del conflicto 2"]
[/CONFLICTOS]`;

  // Historial de conversación (últimos 8 turnos para no saturar)
  const historyText = chatHistory.slice(-8)
    .map(m => `${m.role === 'user' ? 'Usuario' : 'Asistente'}: ${m.content}`)
    .join('\n');

  const prompt = `${historyText ? historyText + '\n\n' : ''}Usuario: ${userMessage}`;

  try {
    const resultText = await callGemini(prompt, systemInstruction, null);

    // Extraer eventos propuestos del bloque [EVENTOS]...[/EVENTOS]
    let eventos = [];
    const eventosMatch = resultText.match(/\[EVENTOS\]\s*([\s\S]*?)\s*\[\/EVENTOS\]/);
    if (eventosMatch) {
      try {
        const raw = eventosMatch[1].replace(/[\x00-\x1f\x7f]/g, ' ').replace(/,\s*([}\]])/g, '$1');
        eventos = JSON.parse(raw);
      } catch { /* ignorar si no parsea */ }
    }

    // Extraer conflictos del bloque [CONFLICTOS]...[/CONFLICTOS]
    let conflictos = [];
    const conflictosMatch = resultText.match(/\[CONFLICTOS\]\s*([\s\S]*?)\s*\[\/CONFLICTOS\]/);
    if (conflictosMatch) {
      try {
        conflictos = JSON.parse(conflictosMatch[1]);
      } catch { /* ignorar */ }
    }

    // Respuesta limpia: quitar los bloques técnicos
    const respuesta = resultText
      .replace(/\[EVENTOS\][\s\S]*?\[\/EVENTOS\]/g, '')
      .replace(/\[CONFLICTOS\][\s\S]*?\[\/CONFLICTOS\]/g, '')
      .trim();

    return { respuesta, eventos, conflictos };
  } catch (error) {
    console.error('Error en chatWithCalendarAI:', error);
    throw new Error(`Error de chat: ${error.message}`);
  }
};

/**
 * Genera el horario completo de un curso a partir de la fecha de la primera masterclass.
 * @param {Object} course - Datos del curso (name, masterclassGap, regularFrequency, etc.)
 * @param {Object} instructor - Datos del instructor (name, color, diasBloqueados)
 * @param {string} firstMCDateTime - ISO string "YYYY-MM-DDTHH:mm:00" de la primera masterclass
 * @param {Array} existingEvents - Eventos existentes en el calendario para detectar conflictos
 * @returns {Object} { resumen, conflictos, eventos }
 */
export const generateCourseSchedule = async (course, instructor, firstMCDateTime, existingEvents = []) => {
  const today = new Date();

  // Eventos futuros relevantes (próximos 90 días) en formato compacto para conflictos
  const future90 = new Date(today);
  future90.setDate(future90.getDate() + 90);
  const relevantEvents = existingEvents
    .filter(ev => {
      const s = ev.start instanceof Date ? ev.start : new Date(ev.start);
      return s >= today && s <= future90;
    })
    .slice(0, 150)
    .map(ev => {
      const s = ev.start instanceof Date ? ev.start : new Date(ev.start);
      const e = ev.end instanceof Date ? ev.end : new Date(ev.end);
      return `${(ev.title || '').slice(0, 45)}|${s.toISOString().slice(0, 16)}-${e.toISOString().slice(11, 16)}`;
    })
    .join('\n');

  const masterclassColor = instructor?.color || '#8b5cf6';
  const gap = course.masterclassGap || 7;
  const phaseDays = course.masterphaseDuration || 30;
  const totalMC = Math.max(2, Math.round(phaseDays / gap));
  const freq = course.regularFrequency || 2;
  const mcHours = course.masterclassHours || 2;
  const sesHours = course.regularHours || 2;
  const hasRegular = course.hasRegular !== false;
  const hasMC = course.hasMasterclass !== false;

  const diasBloqueados = (instructor?.diasBloqueados || [])
    .map(d => (typeof d === 'object' ? d.fecha : d))
    .filter(Boolean);

  const systemInstruction = `Eres un agente experto en programación académica. Tu tarea es generar el horario completo de un curso a partir de una fecha inicial.

REGLAS ABSOLUTAS:
1. De lunes a viernes: MÁXIMO 1 masterclass Y 1 clase por día. NUNCA 2 masterclasses el mismo día.
2. Entre masterclasses del mismo curso: mínimo ${gap} días de separación.
3. Masterclasses: misma hora que la primera masterclass indicada.
4. Clases regulares: ${freq} veces por semana, días consistentes (Lun/Mié/Vie o Mar/Jue según disponibilidad), misma hora todos los días.
5. NO programar en días bloqueados del instructor: [${diasBloqueados.join(', ') || 'ninguno'}].
6. Si ya hay un evento existente a la misma fecha+hora: reportarlo como conflicto pero igual incluirlo en la lista (marcado con "⚠" en el título).
7. Generar EXACTAMENTE ${totalMC} masterclasses (si el curso tiene masterclass).
8. Si el curso tiene clases regulares: generar sesiones ${freq} veces/semana durante ${phaseDays} días.

NOMENCLATURA OBLIGATORIA:
- Masterclass: "1M - ${course.name}", "2M - ${course.name}", ... hasta "${totalMC}M - ${course.name}"
- Clase regular: "SESION 1 - ${course.name}", "SESION 2 - ${course.name}", ...

COLORES:
- Masterclass: "${masterclassColor}"
- Clase regular: "#3b82f6"`;

  const prompt = `CURSO A PROGRAMAR:
- Nombre: ${course.name}
- Instructor: ${instructor?.name || 'Sin asignar'}
- Tiene Masterclass: ${hasMC ? 'SÍ' : 'NO'}
- Tiene Clases Regulares: ${hasRegular ? 'SÍ' : 'NO'}
- Duración Masterclass: ${mcHours}h
- Duración Clase Regular: ${sesHours}h
- Gap entre Masterclasses: ${gap} días
- Frecuencia Clases: ${freq} veces/semana
- Duración de la fase: ${phaseDays} días
- Total masterclasses a generar: ${totalMC}

PRIMERA MASTERCLASS: ${firstMCDateTime}

EVENTOS EXISTENTES EN EL CALENDARIO (próximos 90 días, formato TITULO|INICIO-FIN):
${relevantEvents || '(calendario vacío)'}

Genera el horario completo. Responde ÚNICAMENTE con este bloque JSON (sin texto adicional):
[HORARIO]
{
  "resumen": "descripción breve del horario generado",
  "conflictos": ["descripción conflicto 1", ...],
  "eventos": [
    {"titulo": "1M - ${course.name}", "inicio": "YYYY-MM-DDTHH:mm:00", "fin": "YYYY-MM-DDTHH:mm:00", "tipo": "masterclass", "color": "${masterclassColor}"},
    {"titulo": "SESION 1 - ${course.name}", "inicio": "YYYY-MM-DDTHH:mm:00", "fin": "YYYY-MM-DDTHH:mm:00", "tipo": "clase", "color": "#3b82f6"}
  ]
}
[/HORARIO]`;

  const raw = await callGemini(prompt, systemInstruction, null);

  // Extraer bloque [HORARIO]...[/HORARIO]
  const match = raw.match(/\[HORARIO\]\s*([\s\S]*?)\s*\[\/HORARIO\]/);
  let jsonStr = match ? match[1] : raw;

  // Fallback: buscar JSON directo
  if (!match) {
    const jsonMatch = jsonStr.match(/\{[\s\S]*\}/);
    if (jsonMatch) jsonStr = jsonMatch[0];
  }

  const tryFixes = [
    (s) => s,
    (s) => s.replace(/,\s*([}\]])/g, '$1'),
    (s) => s.replace(/[\x00-\x1f\x7f]/g, ' '),
    (s) => s.replace(/[\x00-\x1f\x7f]/g, ' ').replace(/,\s*([}\]])/g, '$1'),
  ];

  for (const fix of tryFixes) {
    try {
      const parsed = JSON.parse(fix(jsonStr));
      return {
        resumen: parsed.resumen || '',
        conflictos: parsed.conflictos || [],
        eventos: (parsed.eventos || []).map(ev => ({
          titulo: ev.titulo || ev.title || '',
          inicio: ev.inicio || ev.start || '',
          fin: ev.fin || ev.end || '',
          tipo: ev.tipo || ev.type || 'masterclass',
          color: ev.color || masterclassColor,
        })),
      };
    } catch { /* continuar */ }
  }

  throw new Error('No se pudo parsear el horario generado. Intenta de nuevo.');
};